/**
 * Flipped classroom (M5) — drizzle query builders for the class session plan.
 * Local-first consumption over the cohort streams (session_module rides along
 * with my_cohorts_student/teacher/campus/org). Writes via write-path (mutations.ts).
 */

import {
  and,
  asc,
  assignment,
  class_session,
  course_module,
  eq,
  isNull,
  lesson,
  type PowerSyncDrizzleDB,
  resource,
  session_module,
  video_asset,
} from "@space/powersync";

export const sessionPlanQueryKeys = {
  bySession: (sessionId: string | undefined) => ["session-plan", "session", sessionId] as const,
  byCohort: (cohortId: string | undefined) => ["session-plan", "cohort", cohortId] as const,
  sessionRow: (sessionId: string | undefined) =>
    ["session-plan", "session-row", sessionId] as const,
  directory: (courseId: string | undefined) => ["session-plan", "directory", courseId] as const,
  myProgress: (userId: string | undefined, courseId: string | undefined) =>
    ["session-plan", "progress", userId, courseId] as const,
  mySubmissions: (userId: string | undefined) => ["session-plan", "submissions", userId] as const,
  myWatch: (userId: string | undefined) => ["session-plan", "watch", userId] as const,
};

/** A session's (active) plan items, ordered by phase and position. */
export function getSessionModulesBySessionQuery(db: PowerSyncDrizzleDB, sessionId: string) {
  return db
    .select()
    .from(session_module)
    .where(and(eq(session_module.class_session_id, sessionId), isNull(session_module.deleted_at)))
    .orderBy(asc(session_module.phase), asc(session_module.position));
}

/** All plan items for a cohort (for "has a plan" badges in the schedule). */
export function getSessionModulesByCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(session_module)
    .where(and(eq(session_module.cohort_id, cohortId), isNull(session_module.deleted_at)));
}

/** A class_session by id (for the header of the student's plan view). */
export function getClassSessionRowQuery(db: PowerSyncDrizzleDB, sessionId: string) {
  return db
    .select()
    .from(class_session)
    .where(and(eq(class_session.id, sessionId), isNull(class_session.deleted_at)))
    .limit(1);
}

export interface CourseModuleDirectoryRow {
  id: string;
  kind: string | null;
  ref_id: string | null;
  lesson_title: string | null;
  resource_title: string | null;
  assignment_title: string | null;
  video_title: string | null;
}

/**
 * Directory of a course's modules with the resolved TITLE of its detail entity
 * (lesson/resource/assignment/video). A single local-first query: the ref_id is unique
 * per table, so the left-joins don't expand rows. The UI composes the title by kind and
 * uses it to render the plan and the "add module" selector.
 */
export function getCourseModuleDirectoryQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db
    .select({
      id: course_module.id,
      kind: course_module.kind,
      ref_id: course_module.ref_id,
      lesson_title: lesson.title,
      resource_title: resource.title,
      assignment_title: assignment.title,
      video_title: video_asset.title,
    })
    .from(course_module)
    .leftJoin(lesson, eq(course_module.ref_id, lesson.id))
    .leftJoin(resource, eq(course_module.ref_id, resource.id))
    .leftJoin(assignment, eq(course_module.ref_id, assignment.id))
    .leftJoin(video_asset, eq(course_module.ref_id, video_asset.id))
    .where(and(eq(course_module.course_id, courseId), isNull(course_module.deleted_at)))
    .orderBy(asc(course_module.position));
}

/** Resolves a directory row's title according to its kind. */
export function directoryRowTitle(row: CourseModuleDirectoryRow): string | null {
  switch (row.kind) {
    case "lesson": {
      return row.lesson_title;
    }
    case "resource": {
      return row.resource_title;
    }
    case "assignment": {
      return row.assignment_title;
    }
    case "video": {
      return row.video_title;
    }
    default: {
      return null;
    }
  }
}
